import { useState, useEffect, useRef, useCallback } from 'react'
import { getEntityWithChildrenPaginatedQuery } from 'api/queries'
import { moveSingleReaderMutation } from 'api/mutations'

interface Entity {
  id?: number
  name?: string
  children?: Entity[]
  childrenCount?: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ancestryDetails?: any
}

interface NavigationState {
  id: number
  selectedItems: Entity[]
  renderedChildren: Entity[]
  selectedParent: Entity | null
  totalPages: number
  currentPage: number
}

const useMoveUser = ({ client, currentEntity }) => {
  const [selectedItems, setSelectedItems] = useState<Entity[]>([])
  const [selectedParent, setSelectedParent] = useState<Entity | null>(null)
  const [renderedChildren, setRenderedChildren] = useState<Entity[]>([])
  const [destinationEntity, setDestinationEntity] = useState<Entity>(null)

  const containerRef = useRef(null)
  const isLoadingMoreEntities = useRef(false)
  const currentItemsPage = useRef(1)
  const currentRenderedMainEntityId = useRef(0)
  const totalItemsPages = useRef(0)
  const navigationStack = useRef<NavigationState[]>([]) // Pilha para navegação hierárquica

  const handleEntityClick = (entity: Entity) => {
    setDestinationEntity(entity)
  }

  const handleSubmit = (readerId: number, newEntityId: number) => {
    return client
      .mutate({
        mutation: moveSingleReaderMutation,
        variables: {
          entityId: newEntityId,
          readerId
        }
      })
      .then(() => window.location.reload())
      .catch(err => window.alert(`Oops, aconteceu um erro: ${err.message}`))
  }

  const getEntityWithChildrenPaginated = async (id: number, page: number) => {
    return client
      .query({
        query: getEntityWithChildrenPaginatedQuery,
        variables: {
          id,
          page,
          perPage: 20
        }
      })
      .then(res => {
        return res.data.getEntity
      })
      .catch(err => global.alert(`Oops, aconteceu um erro: ${err.message}`))
  }

  useEffect(() => {
    const getEntities = async () => {
      navigationStack.current = []

      const entity = await getEntityWithChildrenPaginated(
        currentEntity.id,
        currentItemsPage.current
      )
      currentRenderedMainEntityId.current = entity.id
      setSelectedItems(entity.children || [])
      totalItemsPages.current = entity.childrenCount / 20
    }

    getEntities()
    currentItemsPage.current = currentItemsPage.current + 1

    if (containerRef.current) {
      containerRef.current.addEventListener('scroll', onScrollContainer)
    }

    return function cleanUp() {
      if (containerRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        containerRef.current.removeEventListener('scroll', onScrollContainer)
      }
      navigationStack.current = []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRenderedChildren = async (item: Entity) => {
    if (!item.childrenCount) return

    const entity = await getEntityWithChildrenPaginated(item.id, 1)

    const currentState: NavigationState = {
      id: currentRenderedMainEntityId.current,
      selectedItems: selectedItems,
      renderedChildren: renderedChildren,
      selectedParent: selectedParent,
      totalPages: totalItemsPages.current,
      currentPage: currentItemsPage.current
    }
    navigationStack.current.push(currentState)

    setDestinationEntity(null)
    setSelectedParent(item)
    setRenderedChildren(entity.children || [])
    setSelectedItems([entity])
    totalItemsPages.current = entity.childrenCount / 20
    currentItemsPage.current = 2
    currentRenderedMainEntityId.current = item.id
  }

  const resetParentEntities = async () => {
    setDestinationEntity(null)

    if (navigationStack.current.length > 0) {
      const previousState = navigationStack.current.pop()!

      setSelectedItems(previousState.selectedItems)
      setRenderedChildren(previousState.renderedChildren)
      setSelectedParent(previousState.selectedParent)
      totalItemsPages.current = previousState.totalPages
      currentItemsPage.current = previousState.currentPage
      currentRenderedMainEntityId.current = previousState.id
    } else {
      setRenderedChildren([])
      setSelectedParent(null)

      const entity = await getEntityWithChildrenPaginated(currentEntity.id, 1)
      setSelectedItems(entity.children || [])

      totalItemsPages.current = entity.childrenCount / 20
      currentItemsPage.current = 2
      currentRenderedMainEntityId.current = currentEntity.id
    }
  }

  const onScrollContainer = useCallback(
    async _evt => {
      if (currentItemsPage.current >= totalItemsPages.current + 1) return

      const totalScrollRemaining =
        containerRef.current.scrollHeight -
        containerRef.current.clientHeight -
        containerRef.current.scrollTop

      const MARGIN = 160

      if (totalScrollRemaining <= MARGIN && !isLoadingMoreEntities.current) {
        isLoadingMoreEntities.current = true

        const entity = await getEntityWithChildrenPaginated(
          currentRenderedMainEntityId.current,
          currentItemsPage.current
        )

        if (currentRenderedMainEntityId.current === currentEntity.id) {
          setSelectedItems(previousState => {
            currentItemsPage.current = currentItemsPage.current + 1
            isLoadingMoreEntities.current = false
            return previousState.concat(entity.children || [])
          })
        } else {
          setRenderedChildren(previousState => {
            currentItemsPage.current = currentItemsPage.current + 1
            isLoadingMoreEntities.current = false
            return previousState.concat(entity.children || [])
          })
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoadingMoreEntities, currentItemsPage, currentRenderedMainEntityId]
  )

  return {
    containerRef,
    destinationEntity,
    selectedItems,
    selectedParent,
    renderedChildren,
    handleEntityClick,
    resetParentEntities,
    handleRenderedChildren,
    handleSubmit
  }
}

export { useMoveUser }
